import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage, LangToggle } from "@/context/LanguageContext";
import { formatApiErrorDetail } from "@/api";

const inputCls =
  "w-full bg-obsidian border border-white/15 px-5 py-4 text-white placeholder:text-white/30 focus:border-volt focus:outline-none transition-colors duration-300";
const labelCls = "block font-jetbrains text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user =
        mode === "login"
          ? await login(form.email, form.password)
          : await register(form.name, form.email, form.password);
      toast.success(mode === "login" ? t("auth.okLogin") : t("auth.okRegister"));
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-[#F5F5F5] font-satoshi flex px-6 py-10" data-testid="auth-page">
      <div className="max-w-md w-full mx-auto my-auto">
        <div className="flex items-center justify-between mb-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/50 hover:text-volt transition-colors text-sm uppercase tracking-widest"
            data-testid="auth-back-link"
          >
            <ArrowLeft size={16} /> {t("auth.back")}
          </Link>
          <LangToggle />
        </div>

        <h1 className="font-cabinet font-black uppercase text-4xl md:text-5xl tracking-tight mb-3">
          {mode === "login" ? t("auth.h1login") : t("auth.h1register")}
        </h1>
        <p className="text-white/50 mb-10">
          {mode === "login" ? t("auth.subLogin") : t("auth.subRegister")}
        </p>

        <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mb-8" data-testid="auth-mode-toggle">
          {["login", "register"].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setMode(m);
                setError("");
              }}
              data-testid={`auth-toggle-${m}`}
              className={`py-3 font-cabinet font-bold uppercase tracking-wide text-sm transition-colors duration-300 ${
                mode === m ? "bg-volt text-obsidian" : "bg-obsidian text-white/50 hover:text-white"
              }`}
            >
              {m === "login" ? t("auth.login") : t("auth.register")}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
          {mode === "register" && (
            <div>
              <label htmlFor="auth-name" className={labelCls}>{t("auth.name")}</label>
              <input id="auth-name" data-testid="auth-name-input" required value={form.name} onChange={set("name")} placeholder={t("auth.namePh")} className={inputCls} />
            </div>
          )}
          <div>
            <label htmlFor="auth-email" className={labelCls}>{t("auth.email")}</label>
            <input id="auth-email" data-testid="auth-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
          </div>
          <div>
            <label htmlFor="auth-password" className={labelCls}>
              {t("auth.password")}{mode === "register" ? t("auth.passwordHint") : ""}
            </label>
            <input id="auth-password" data-testid="auth-password-input" type="password" required minLength={mode === "register" ? 8 : 1} value={form.password} onChange={set("password")} placeholder="••••••••" className={inputCls} />
          </div>
          {error && (
            <p className="text-crimson text-sm" data-testid="auth-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-button"
            className="w-full rounded-full bg-volt text-obsidian font-cabinet font-bold uppercase tracking-wide px-8 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-50"
          >
            {busy ? t("auth.busy") : mode === "login" ? t("auth.login") : t("auth.register")}
          </button>
        </form>

        {mode === "login" && (
          <Link to="/reset-password" data-testid="auth-forgot-link" className="block mt-6 text-sm text-white/40 hover:text-volt transition-colors">
            {t("auth.forgot")}
          </Link>
        )}
      </div>
    </div>
  );
}
